import { ChangeEvent, useRef, useState } from "react";

import { CloseFullScreenIcon } from "../../icons/CloseFullScreenIcon/CloseFullScreenIcon";
import { UploadIcon } from "../../icons/UploadIcon/UploadIcon";
import { truncateFileName } from "../../utils/truncateFileName";
import Modal from "../Modal/Modal";

import styles from "./multi-document-uploader.module.css";

type MultiDocumentUploaderProps = {
  label: string;
  placeholder: string;
  loading?: boolean;
  onFilesSelect: (files: File[]) => void;
  onDeleteDocuments: (ids: string) => void;
  editMode?: boolean;
  addSBKUser?: boolean;
  initialFiles?: File[];
};

export const MultiDocumentUploader = ({
  label,
  placeholder,
  onFilesSelect,
  onDeleteDocuments,
  editMode,
  addSBKUser,
  initialFiles = [],
}: MultiDocumentUploaderProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [selectedFilesInitial, setSelectedFilesInitial] = useState<any[]>(
    initialFiles || []
  );
  const [openModal, setOpenModal] = useState(false);
  const [selectedFileIndex, setSelectedFileIndex] = useState<number | null>(
    null
  );
  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (editMode || addSBKUser) {
      const files = Array.from(event.target.files || []);
      if (files.length > 0) {
        setSelectedFiles((prevFiles) => [...prevFiles, ...files]);
        onFilesSelect([...selectedFiles, ...files]);
      }
    }
  };
  const handleOpenModal = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();

    setSelectedFileIndex(index);
    setOpenModal(true);
  };

  const handleConfirmDelete = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (selectedFileIndex === null) return;

    setSelectedFilesInitial((prevFiles) => {
      const updatedFiles = prevFiles.filter((_, i) => i !== selectedFileIndex);
      const newId = prevFiles[selectedFileIndex]?.id;

      if (newId) {
        onDeleteDocuments(newId);
      }

      return updatedFiles;
    });

    setOpenModal(false);
    setSelectedFileIndex(null);
  };

  const handleCloseModal = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenModal(false);
  };

  const removeFile = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const updatedFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(updatedFiles);
    onFilesSelect(updatedFiles);
  };

  return (
    <div
      className={`${styles["multi-document-uploader"]} ${
        editMode ? styles["edit-mode"] : ""
      }`}
      onClick={
        editMode || addSBKUser ? () => fileInputRef.current?.click() : undefined
      }
      role="button"
    >
      <Modal
        isOpen={openModal}
        message={"Dokument entfernen"}
        note={
          "Durch das entfernen von Zertifikaten werden auch die historische Daten gelöscht."
        }
        onConfirm={handleConfirmDelete}
        onCancel={handleCloseModal}
        cancelText="Cancel"
        confirmText={"Dokument entfernen"}
        backButtonShow={(e) => handleCloseModal(e)}
        closeButtonColor={"#3D5170"}
        confirmButtonColor={"#CB3844"}
        iconType={"remove"}
      />
      <label
        className={`${styles["document-uploader-button"]} ${
          editMode ? styles["edit-mode"] : ""
        }`}
      >
        <span>{label}</span>
        <UploadIcon />
      </label>

      <div
        className={`${styles["file-list"]} ${
          editMode ? styles["edit-mode"] : ""
        }`}
      >
        {selectedFiles.length > 0 || selectedFilesInitial?.length > 0 ? (
          <>
            {selectedFilesInitial?.map((file, index) => (
              <div
                className={`${styles["file-item"]} ${
                  editMode ? styles["edit-mode"] : ""
                }`}
                key={`initial-${index}`}
              >
                <span>{truncateFileName(file?.name ?? "", 30)}</span>
                {(editMode || addSBKUser) && (
                  <CloseFullScreenIcon
                    onClick={(e) => handleOpenModal(index, e)}
                  />
                )}
              </div>
            ))}

            {selectedFiles.map((file, index) => (
              <div
                className={`${styles["file-item"]} ${
                  editMode ? styles["edit-mode"] : ""
                }`}
                key={`selected-${index}`}
              >
                <span>{truncateFileName(file?.name ?? "", 30)}</span>
                {(editMode || addSBKUser) && (
                  <CloseFullScreenIcon onClick={(e) => removeFile(index, e)} />
                )}
              </div>
            ))}
          </>
        ) : (
          <span className={styles["placeholder-text"]}>{placeholder}</span>
        )}
      </div>

      <input
        type="file"
        multiple
        ref={fileInputRef}
        style={{ display: "none" }}
        onClick={(e) => e.stopPropagation()}
        onChange={handleFileChange}
      />
    </div>
  );
};

export default MultiDocumentUploader;
